import { signal, computed } from '@angular/core';
import {
    TourData,
    FolderData,
    DeliveryNoteData,
} from '@app/shared/models/store-types';
import { ViewType, UserRole } from '@app/shared/models/view-config.interface';
import { EventWithOrgSiteKey } from '@app/stores/eventsStore';

// Union type for all possible selection items
export type SelectableItem =
    | TourData
    | FolderData
    | DeliveryNoteData
    | EventWithOrgSiteKey;

// Context data interface for better type safety
export interface SelectionContextData {
    userRole?: UserRole;
    viewType?: ViewType;
    organizationSiteKey?: string;
    [key: string]: any;
}

export interface SelectionState {
    selectedItems: SelectableItem[];
    selectionType: 'single' | 'multiple';
    lastSelectedItem: SelectableItem | null;
    selectionCount: number;
    contextData?: SelectionContextData;
}

const initialState: SelectionState = {
    selectedItems: [],
    selectionType: 'multiple',
    lastSelectedItem: null,
    selectionCount: 0,
    contextData: undefined,
};

export const selectionState = signal<SelectionState>(initialState);

// Computed values
export const getSelectedItems = computed(() => selectionState().selectedItems);
export const getSelectionCount = computed(
    () => selectionState().selectedItems.length
);
export const getLastSelectedItem = computed(
    () => selectionState().lastSelectedItem
);
export const hasSelection = computed(
    () => selectionState().selectedItems.length > 0
);
export const hasSingleSelection = computed(
    () => selectionState().selectedItems.length === 1
);
export const hasMultipleSelection = computed(
    () => selectionState().selectedItems.length > 1
);
export const getSelectionType = computed(() => selectionState().selectionType);
export const getContextData = computed(() => selectionState().contextData);

// Actions
export const setSelection = (
    items: SelectableItem[],
    contextData?: SelectionContextData
) => {
    selectionState.update(state => ({
        ...state,
        selectedItems: items,
        selectionCount: items.length,
        lastSelectedItem: items.length > 0 ? items[items.length - 1] : null,
        contextData: contextData || state.contextData,
    }));
};

export const addToSelection = (item: SelectableItem) => {
    const currentState = selectionState();
    const itemKey = getItemKey(item);

    const existingItem = currentState.selectedItems.find(
        selectedItem => getItemKey(selectedItem) === itemKey
    );
    if (existingItem) {
        return;
    }

    selectionState.update(state => {
        const newItems = [...state.selectedItems, item];
        return {
            ...state,
            selectedItems: newItems,
            selectionCount: newItems.length,
            lastSelectedItem: item,
        };
    });
};

export const removeFromSelection = (item: SelectableItem) => {
    const itemKey = getItemKey(item);

    selectionState.update(state => {
        const newItems = state.selectedItems.filter(
            selected => getItemKey(selected) !== itemKey
        );

        return {
            ...state,
            selectedItems: newItems,
            selectionCount: newItems.length,
            lastSelectedItem:
                newItems.length > 0 ? newItems[newItems.length - 1] : null,
        };
    });
};

export const toggleSelection = (item: SelectableItem) => {
    const currentItems = selectionState().selectedItems;
    const itemKey = getItemKey(item);

    const isSelected = currentItems.some(
        selectedItem => getItemKey(selectedItem) === itemKey
    );

    if (isSelected) {
        removeFromSelection(item);
    } else {
        addToSelection(item);
    }
};

export const clearSelection = () => {
    selectionState.update(state => ({
        ...state,
        selectedItems: [],
        selectionCount: 0,
        lastSelectedItem: null,
    }));
};

export const setSelectionType = (type: 'single' | 'multiple') => {
    selectionState.update(state => {
        const newSelectedItems =
            type === 'single' && state.selectedItems.length > 1
                ? [state.lastSelectedItem!].filter(Boolean)
                : state.selectedItems;

        return {
            ...state,
            selectionType: type,
            selectedItems: newSelectedItems,
            selectionCount: newSelectedItems.length,
        };
    });
};

export const selectAll = (items: SelectableItem[]) => {
    selectionState.update(state => ({
        ...state,
        selectedItems: [...items],
        selectionCount: items.length,
        lastSelectedItem: items.length > 0 ? items[items.length - 1] : null,
    }));
};

export const isItemSelected = (item: SelectableItem): boolean => {
    const itemKey = getItemKey(item);
    return selectionState().selectedItems.some(
        selectedItem => getItemKey(selectedItem) === itemKey
    );
};

export const setContextData = (contextData: SelectionContextData) => {
    selectionState.update(state => ({
        ...state,
        contextData,
    }));
};

// Type guards for better type safety
export function hasStatus(
    item: SelectableItem
): item is SelectableItem & { status: string } {
    return 'status' in item && typeof item.status === 'string';
}

export function hasReadOnly(
    item: SelectableItem
): item is SelectableItem & { read_only: boolean } {
    return 'read_only' in item && typeof item.read_only === 'boolean';
}

export function hasKey(
    item: SelectableItem
): item is SelectableItem & { _key: string } {
    return '_key' in item && typeof item._key === 'string';
}

export function hasEventKey(
    item: SelectableItem
): item is SelectableItem & { eventKey: string } {
    return 'eventKey' in item && typeof item.eventKey === 'string';
}

export function getItemKey(item: SelectableItem): string {
    if (hasKey(item)) {
        return item._key;
    } else if (hasEventKey(item)) {
        return item.eventKey;
    }
    return 'unknown';
}

// Type guards for specific selection types
export function isFolderData(item: SelectableItem): item is FolderData {
    return 'bundleNumber' in item && 'consigneeKey' in item;
}

export function isTourData(item: SelectableItem): item is TourData {
    return 'tourNumber' in item && 'deliveryNoteKeys' in item;
}

export function isDeliveryNoteData(
    item: SelectableItem
): item is DeliveryNoteData {
    return (
        'number' in item &&
        !('bundleNumber' in item) &&
        !('tourNumber' in item) &&
        !('eventType' in item)
    );
}

export function isEventData(item: SelectableItem): item is EventWithOrgSiteKey {
    return 'eventType' in item || 'eventKey' in item;
}

export function filterFolderData(items: SelectableItem[]): FolderData[] {
    return items.filter(isFolderData);
}

export function filterTourData(items: SelectableItem[]): TourData[] {
    return items.filter(isTourData);
}

export function filterDeliveryNoteData(
    items: SelectableItem[]
): DeliveryNoteData[] {
    return items.filter(isDeliveryNoteData);
}

export function filterEventData(
    items: SelectableItem[]
): EventWithOrgSiteKey[] {
    return items.filter(isEventData);
}

export const isSelectionValidForCheckin = computed(() => {
    const state = selectionState();
    const selectedItems = state.selectedItems;

    return (
        selectedItems.length > 0 &&
        selectedItems.every(item => {
            const status = hasStatus(item) ? item.status : null;
            const readOnly = hasReadOnly(item) ? item.read_only : false;
            return status !== 'closed' && !readOnly;
        })
    );
});

export const getCheckinReadyCount = computed(() => {
    const state = selectionState();
    const selectedItems = state.selectedItems;

    return selectedItems.filter(item => {
        const status = hasStatus(item) ? item.status : null;
        const readOnly = hasReadOnly(item) ? item.read_only : false;
        return status !== 'closed' && !readOnly;
    }).length;
});
